import { Fragment as _Fragment, jsx as _jsx } from "@emotion/react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { initializeSnowplow, trackPageViewWithDebounce } from './utils';
const useSnowplowTracking = (props) => {
    const { appId, userId, trackerName, pageViewContext, pluginsOverride, snowplowEndpointOverride } = props;
    const tracker = useRef(null);
    const lastTrackedTime = useRef(0);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const location = props.locationOverride ?? useLocation();
    const [initialized, setInitialized] = useState(false);
    const initStarted = useRef(false);
    useEffect(() => {
        if (initStarted.current) {
            return;
        }
        initStarted.current = true;
        const init = async () => {
            if (typeof window !== 'undefined') {
                try {
                    await initializeSnowplow({
                        tracker,
                        appId,
                        trackerName,
                        userId,
                        pluginsOverride,
                        snowplowEndpointOverride
                    });
                    setInitialized(true);
                }
                catch (error) {
                    console.error('Snowplow initialization failed:', error);
                }
            }
        };
        init().catch(console.error);
    }, []);
    useEffect(() => {
        if (initialized) {
            trackPageViewWithDebounce(trackerName, lastTrackedTime, pageViewContext);
        }
    }, [location.pathname, initialized, trackerName, pageViewContext]);
    return tracker;
};
export const Snowplow = ({ children, ...props }) => {
    useSnowplowTracking(props);
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return _jsx(_Fragment, { children: children });
};
/**
 * Helper function for conditional Snowplow wrapping.
 * @param component - React component to potentially wrap with Snowplow.
 * @param snowPlowProps - Props for the Snowplow component (excluding 'children').
 * @param enabled - Boolean flag to determine if Snowplow should be enabled.
 * @returns The component wrapped with Snowplow if enabled, otherwise the original component.
 *
 * This function allows for conditional application of Snowplow tracking based on
 * a feature flag or other runtime conditions. It's useful for gradually rolling out
 * Snowplow tracking or enabling/disabling it in different environments.
 *
 * Tracking features enabled by default:
 * - Button click tracking: Tracks user interactions with buttons.
 * - Link click tracking: Monitors clicks on links within the application.
 * - Activity tracking: Monitors user activity on the page e.g. is the tab in focus, does the mouse move over the page, does the user scroll, is updatePageActivity called, etc.
 * - Form tracking: Tracks form interactions.
 */
export const WithSnowplow = (component, snowPlowProps, enabled) => {
    if (enabled) {
        return _jsx(Snowplow, { ...snowPlowProps, children: component });
    }
    return component;
};
export default Snowplow;
//# sourceMappingURL=data:application/json;base64,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